function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART15: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 176. Newsletter Non-Click Follow-Up (Pattern I) ──────────────────

  "Newsletter Non-Click Follow-Up": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber opened newsletter but did not click",
        "webhook_app",
        {
          source: "mailchimp",
          event: "campaign.opened_no_click",
          howItStarts:
            "Fires when a Mailchimp campaign is opened by a subscriber but no link click is recorded within 24 hours of the open event.",
          setup:
            "Connect your Mailchimp account and configure a webhook for the campaign open event. The workflow filters for subscribers who opened but did not click any link.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract newsletter topic, subscriber segment, and open context",
        280,
        "You are a newsletter engagement analyst. Parse the webhook payload and extract subscriber_email, subscriber_name, campaign_id, campaign_subject, send_date, open_timestamp, subscriber_segment, newsletter_topic, and key_links (array of URLs from the newsletter).",
        "Analyze this newsletter open-no-click event: Campaign ID: {campaign_id}, Subject: {campaign_subject}, Subscriber: {subscriber_email}, Segment: {subscriber_segment}. Extract structured engagement context.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "campaign_subject", as: "campaign_subject", type: "string" },
          { field: "newsletter_topic", as: "newsletter_topic", type: "string" },
          {
            field: "subscriber_segment",
            as: "subscriber_segment",
            type: "string",
          },
          { field: "key_links", as: "key_links", type: "object" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber engagement history and tags",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}/activity",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "activity[0].action",
                customName: "last_action",
                type: "string",
              },
              {
                name: "total_items",
                customName: "total_activities",
                type: "number",
              },
              {
                name: "activity",
                customName: "engagement_history",
                type: "object",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Subscriber clicked any newsletter in last 30 days?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "total_activities",
                operator: "greater_than",
                value: "5",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send re-angled follow-up with different subject line and CTA",
        1000,
        "{subscriber_email}",
        "You missed this from our latest: {newsletter_topic}",
        "Hi {subscriber_name},\\n\\nWe noticed you opened our latest newsletter about {newsletter_topic} but didn't get a chance to explore the content. Here's a quick summary of the top highlights:\\n\\n{key_links}\\n\\nWe've picked the most relevant piece for you based on your interests. Click below to dive in.\\n\\nBest,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 3 days for click engagement", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Clicked the follow-up email?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "last_action", operator: "equals", value: "click" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send alternative content format (video recap or infographic)",
        1720,
        "{subscriber_email}",
        "Try a different format: {newsletter_topic} in 2 minutes",
        "Hi {subscriber_name},\\n\\nSome people prefer a quick visual over a long read. Here's our {newsletter_topic} recap in a 2-minute video and downloadable infographic.\\n\\nWatch the recap: [Video Link]\\nDownload the infographic: [PDF Link]\\n\\nLet us know which format you prefer by updating your preferences.\\n\\nCheers,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Recovers newsletter engagement from subscribers who opened but did not click. Extracts campaign topic and subscriber context via AI, checks Mailchimp engagement history, sends a re-angled follow-up email with a different subject line and curated highlights, waits 3 days, and if still no click, sends alternative content in video or infographic format to match subscriber preferences.",
  },

  // ─── 177. New Collection Launch (Pattern D) ───────────────────────────

  "New Collection Launch": {
    nodes: [
      trigger(
        "Mailchimp webhook: New product collection published in store",
        "webhook_app",
        {
          source: "mailchimp",
          event: "store.collection_published",
          howItStarts:
            "Fires when a new product collection is published in the connected e-commerce store, triggering Mailchimp to notify subscribers who match the collection category.",
          setup:
            "Connect your Mailchimp account with your e-commerce store integration. Enable the store webhook for new collection publish events.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract collection name, category, hero products, and price range",
        280,
        "You are an e-commerce merchandising analyst. Parse the new collection webhook and extract collection_name, collection_category, hero_products (array with name, price, image_url), price_range_min, price_range_max, launch_date, collection_description, and target_audience_tags (array).",
        "Analyze this new collection launch event: Collection data from webhook payload. Extract structured collection details including hero products, pricing, and target audience.",
        [
          { field: "collection_name", as: "collection_name", type: "string" },
          {
            field: "collection_category",
            as: "collection_category",
            type: "string",
          },
          { field: "hero_products", as: "hero_products", type: "object" },
          { field: "price_range_min", as: "price_range_min", type: "number" },
          { field: "price_range_max", as: "price_range_max", type: "number" },
          {
            field: "target_audience_tags",
            as: "target_audience_tags",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Verify subscriber interest tags and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/segments/{segment_id}/members",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "total_items",
                customName: "matched_subscribers",
                type: "number",
              },
              {
                name: "members[0].email_address",
                customName: "subscriber_email",
                type: "string",
              },
              {
                name: "members[0].merge_fields.FNAME",
                customName: "subscriber_name",
                type: "string",
              },
              {
                name: "members[0].tags_count",
                customName: "interest_tags_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Collection has 3+ hero products and matches subscriber interest?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "matched_subscribers",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send new collection launch email with hero products and shop links",
        1000,
        "{subscriber_email}",
        "Just dropped: The {collection_name} Collection",
        "Hi {subscriber_name},\\n\\nWe're thrilled to introduce our brand new {collection_name} collection! Curated just for you based on your love of {collection_category}.\\n\\nFeatured pieces starting from ${price_range_min}:\\n\\n{hero_products}\\n\\nBe among the first to shop the full collection before your favorites sell out.\\n\\n[Shop the Collection]\\n\\nHappy shopping,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 2 days for purchase activity", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "Subscriber purchased from new collection?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "interest_tags_count",
              operator: "greater_than",
              value: "2",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send styling tips and bestsellers follow-up for collection",
        1720,
        "{subscriber_email}",
        "How to style the {collection_name} Collection — plus bestsellers",
        "Hi {subscriber_name},\\n\\nOur {collection_name} collection is flying off the shelves! Here are the top sellers so far and styling inspiration to help you make the most of your picks.\\n\\nTop sellers: {hero_products}\\n\\nStyling tips from our team:\\n- Pair the [item] with [item] for a casual look\\n- Dress it up with [item] for evening wear\\n\\nDon't miss out — some sizes are already running low.\\n\\n[Shop Bestsellers]\\n\\nCheers,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Announces a new product collection launch to interested Mailchimp subscribers. AI extracts collection details including hero products and pricing, verifies subscriber interest tags and purchase history, sends a launch email featuring hero products and direct shop links, waits 2 days, and follows up with styling tips and bestseller highlights for subscribers who haven't purchased yet.",
  },

  // ─── 178. Gift Guide Nurture (Pattern I) ──────────────────────────────

  "Gift Guide Nurture": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber opens or clicks holiday gift guide email",
        "webhook_app",
        {
          source: "mailchimp",
          event: "campaign.gift_guide_engagement",
          howItStarts:
            "Fires when a subscriber engages with a holiday gift guide campaign by opening the email or clicking a gift category link, indicating active gift-shopping interest.",
          setup:
            "Connect your Mailchimp account and tag your gift guide campaigns. Configure the webhook to fire on open or click events for campaigns tagged 'gift-guide'.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract gift categories browsed, price preferences, and recipient types",
        280,
        "You are a holiday shopping analyst. Parse the gift guide engagement data and extract subscriber_email, subscriber_name, clicked_categories (array), price_preference (budget/mid-range/premium), recipient_types (array: him/her/kids/parents/coworker), engagement_level (opened_only/clicked), and gift_guide_version.",
        "Analyze this gift guide engagement: Subscriber interacted with holiday gift guide campaign. Extract shopping intent signals including categories, price range, and gift recipients.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "clicked_categories",
            as: "clicked_categories",
            type: "object",
          },
          { field: "price_preference", as: "price_preference", type: "string" },
          { field: "recipient_types", as: "recipient_types", type: "object" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber purchase history and saved gift lists",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "orders_count",
                customName: "past_orders_count",
                type: "number",
              },
              {
                name: "total_spent",
                customName: "total_spent",
                type: "number",
              },
              { name: "first_name", customName: "first_name", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Subscriber clicked specific gift categories (not just opened)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "engagement_level",
                operator: "equals",
                value: "clicked",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send personalized gift picks based on clicked categories and budget",
        1000,
        "{subscriber_email}",
        "Handpicked gifts in {clicked_categories} — just for your list",
        "Hi {first_name},\\n\\nWe saw you checking out gifts in {clicked_categories}! Great taste. Here are our top picks in your {price_preference} price range, perfect for {recipient_types}.\\n\\nCurated picks:\\n- [Product 1 — $XX] Perfect for [recipient]\\n- [Product 2 — $XX] A crowd-pleaser\\n- [Product 3 — $XX] Our team's favorite\\n\\nOrder by Dec 18 for guaranteed holiday delivery.\\n\\n[Shop These Picks]\\n\\nHappy gifting,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 4 days for gift purchase activity", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Subscriber made a gift purchase?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "past_orders_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send complementary gift category picks and last-minute gift ideas",
        1720,
        "{subscriber_email}",
        "Still shopping? New gift ideas + last-minute picks",
        "Hi {first_name},\\n\\nThe holidays are getting closer! Here are some fresh gift ideas we think you'll love — including categories you haven't explored yet.\\n\\nNew categories to browse:\\n- Stocking stuffers under $25\\n- Experience gifts they'll remember\\n- Personalized gifts with a special touch\\n\\nPlus, check out our last-minute gift guide with items that ship in 2 days or less.\\n\\n[Browse New Gift Ideas]\\n\\nWe've got you covered,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Nurtures holiday gift shoppers who engaged with a Mailchimp gift guide campaign. AI extracts browsed categories, price preferences, and recipient types, fetches subscriber purchase history, sends personalized gift picks matched to their clicked categories and budget, waits 4 days, and follows up with complementary category suggestions and last-minute gift ideas for those who haven't yet purchased.",
  },

  // ─── 179. Holiday Countdown Journey (Pattern I) ───────────────────────

  "Holiday Countdown Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Holiday countdown campaign sequence initiated for subscriber",
        "webhook_app",
        {
          source: "mailchimp",
          event: "automation.holiday_countdown_start",
          howItStarts:
            "Fires when a subscriber is enrolled into a holiday countdown email sequence, typically triggered by segment membership during the holiday shopping window (e.g., Black Friday, Christmas, Valentine's Day).",
          setup:
            "Connect your Mailchimp account and create a holiday countdown automation. Tag subscribers for enrollment based on engagement history or segment criteria.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract holiday type, countdown start, subscriber preferences, and deal tiers",
        280,
        "You are a holiday marketing strategist. Parse the countdown enrollment data and extract subscriber_email, subscriber_name, holiday_name (e.g., Black Friday, Christmas), countdown_start_date, days_until_holiday, subscriber_purchase_tier (new/returning/vip), preferred_categories (array), and deal_sensitivity (deal-seeker/full-price-buyer).",
        "Analyze this holiday countdown enrollment: Subscriber enrolled for holiday promotions. Extract holiday context, subscriber tier, preferences, and deal sensitivity.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "holiday_name", as: "holiday_name", type: "string" },
          {
            field: "days_until_holiday",
            as: "days_until_holiday",
            type: "number",
          },
          {
            field: "subscriber_purchase_tier",
            as: "subscriber_purchase_tier",
            type: "string",
          },
          {
            field: "preferred_categories",
            as: "preferred_categories",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber lifetime value and last purchase date",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "total_spent",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "orders_count",
                customName: "total_orders",
                type: "number",
              },
              { name: "first_name", customName: "first_name", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "More than 7 days until holiday (early vs. last-minute)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "days_until_holiday",
                operator: "greater_than",
                value: "7",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send early-bird countdown email with exclusive preview deals",
        1000,
        "{subscriber_email}",
        "{holiday_name} is {days_until_holiday} days away — your early access starts now",
        "Hi {first_name},\\n\\nThe countdown to {holiday_name} is ON! With {days_until_holiday} days to go, we're giving you an exclusive early look at our biggest deals.\\n\\nYour VIP early access:\\n- Up to 40% off {preferred_categories}\\n- Free shipping on orders over $50\\n- Exclusive bundles only available this week\\n\\nDon't wait — early-bird pricing ends in 3 days.\\n\\n[Shop Early Deals]\\n\\nHappy shopping,\\nThe Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 3 days for engagement and purchase check",
        1240,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Subscriber has engaged (opened/clicked) or purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "total_orders", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send urgency countdown email with top deals and shipping deadline",
        1720,
        "{subscriber_email}",
        "Only a few days left! {holiday_name} top deals + shipping deadline",
        "Hi {first_name},\\n\\nTime is running out! {holiday_name} is almost here, and these are the deals our customers are loving most.\\n\\nTrending now:\\n- [Top Deal 1] — 35% off (selling fast)\\n- [Top Deal 2] — Buy 2, Get 1 free\\n- [Top Deal 3] — Limited edition bundle\\n\\nShipping deadline: Order by [date] to guarantee delivery before {holiday_name}.\\n\\nLast chance — prices go back up at midnight.\\n\\n[Shop Final Deals]\\n\\nDon't miss out,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Drives holiday sales through a timed countdown email journey in Mailchimp. AI extracts holiday type, countdown timing, and subscriber preferences, fetches lifetime value and purchase history, sends early-bird preview deals to subscribers with time remaining, waits 3 days, and follows up with urgency-driven countdown emails featuring trending deals and shipping deadlines for those who haven't purchased.",
  },

  // ─── 180. Coupon Expiry Reminder (Pattern D) ──────────────────────────

  "Coupon Expiry Reminder": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber's coupon is approaching expiration",
        "webhook_app",
        {
          source: "mailchimp",
          event: "promo.coupon_expiring",
          howItStarts:
            "Fires when a subscriber's assigned coupon code is within 3 days of its expiration date and has not yet been redeemed, prompting a reminder sequence.",
          setup:
            "Connect your Mailchimp account with your e-commerce store. Set up a webhook or automation trigger that monitors coupon expiration dates and fires when a coupon is 3 days from expiry.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract coupon code, discount amount, expiry date, and eligible products",
        280,
        "You are a promotional offer analyst. Parse the coupon expiry webhook and extract subscriber_email, subscriber_name, coupon_code, discount_type (percentage/fixed), discount_amount, expiry_date, hours_until_expiry, minimum_order_value, eligible_categories (array), and coupon_origin (welcome/loyalty/campaign).",
        "Analyze this coupon expiry event: A subscriber's coupon is nearing expiration. Extract full coupon details, eligibility rules, and subscriber context.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "coupon_code", as: "coupon_code", type: "string" },
          { field: "discount_amount", as: "discount_amount", type: "string" },
          {
            field: "hours_until_expiry",
            as: "hours_until_expiry",
            type: "number",
          },
          {
            field: "eligible_categories",
            as: "eligible_categories",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Verify coupon is still active and check subscriber purchase status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/promo-rules/{promo_rule_id}/promo-codes/{promo_code_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "usage_count", customName: "times_used", type: "number" },
              { name: "enabled", customName: "coupon_active", type: "boolean" },
              {
                name: "redemption_url",
                customName: "redemption_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Coupon still active and unredeemed?", 760, {
        conditionDetails: {
          conditions: [
            { field: "coupon_active", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send coupon expiry reminder with product recommendations",
        1000,
        "{subscriber_email}",
        "Your {discount_amount} off coupon expires soon — don't miss out!",
        "Hi {subscriber_name},\\n\\nJust a friendly reminder: your exclusive coupon code {coupon_code} for {discount_amount} off is expiring in less than {hours_until_expiry} hours!\\n\\nHere are some top picks to use it on:\\n- [Recommended Product 1] in {eligible_categories}\\n- [Recommended Product 2] — a customer favorite\\n- [Recommended Product 3] — new arrival\\n\\nUse code: {coupon_code} at checkout.\\n\\n[Shop Now & Save]\\n\\nDon't let this one slip away,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 24 hours until close to final expiry", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Coupon still unredeemed with less than 24 hours left?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "times_used", operator: "equals", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send final last-chance email with countdown urgency",
        1720,
        "{subscriber_email}",
        "FINAL HOURS: Your {discount_amount} off coupon expires tonight",
        "Hi {subscriber_name},\\n\\nThis is your last chance! Your coupon code {coupon_code} for {discount_amount} off expires in just a few hours.\\n\\nHere's what you could save:\\n- [Best Seller] — was $89, now ${reduced_price} with your coupon\\n- [Popular Item] — save {discount_amount} instantly\\n\\nOnce it's gone, it's gone. Use code {coupon_code} at checkout before midnight.\\n\\n[Use My Coupon Now]\\n\\nLast chance,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Recovers unused coupons before expiration through a Mailchimp reminder sequence. AI extracts coupon details including discount amount, eligible categories, and hours until expiry, verifies the coupon is still active and unredeemed, sends a reminder with personalized product recommendations, waits 24 hours, and sends a final last-chance urgency email with specific savings examples for subscribers who still haven't redeemed.",
  },

  // ─── 181. First Purchase Converter (Pattern E) ────────────────────────

  "First Purchase Converter": {
    nodes: [
      trigger(
        "Mailchimp webhook: New subscriber has not made first purchase after 7 days",
        "webhook_app",
        {
          source: "mailchimp",
          event: "subscriber.no_first_purchase",
          howItStarts:
            "Fires when a new Mailchimp subscriber who signed up at least 7 days ago has not yet made their first purchase, triggering a conversion-focused nurture sequence.",
          setup:
            "Connect your Mailchimp account with your e-commerce store. Configure an automation that monitors new subscribers and triggers when no purchase is recorded within 7 days of signup.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract subscriber profile, signup source, and browsing interests",
        280,
        "You are a conversion optimization analyst. Parse the new subscriber data and extract subscriber_email, subscriber_name, signup_date, days_since_signup, signup_source (popup/footer/checkout/landing-page), browsed_categories (array based on any tracked activity), email_opens_count, and any_cart_activity (boolean).",
        "Analyze this first-purchase conversion opportunity: New subscriber without purchase after 7 days. Extract subscriber profile and engagement signals to craft a targeted conversion email.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "browsed_categories",
            as: "browsed_categories",
            type: "object",
          },
          {
            field: "email_opens_count",
            as: "email_opens_count",
            type: "number",
          },
          {
            field: "any_cart_activity",
            as: "any_cart_activity",
            type: "boolean",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber engagement stats and e-commerce activity",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "stats.avg_open_rate",
                customName: "avg_open_rate",
                type: "number",
              },
              {
                name: "stats.avg_click_rate",
                customName: "avg_click_rate",
                type: "number",
              },
              {
                name: "merge_fields.FNAME",
                customName: "first_name",
                type: "string",
              },
              { name: "tags_count", customName: "tags_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Subscriber is engaged (opened emails or browsed products)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "email_opens_count",
                operator: "greater_than",
                value: "1",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send first-purchase nudge with bestsellers and social proof",
        1000,
        "{subscriber_email}",
        "Here's what our newest customers are loving, {first_name}",
        "Hi {first_name},\\n\\nWelcome to the family! We noticed you've been checking us out, and we wanted to make sure you see what everyone's raving about.\\n\\nOur most-loved products:\\n- [Bestseller 1] — 4.8 stars, 2,000+ reviews\\n- [Bestseller 2] — 'Can't live without it' — Sarah M.\\n- [Bestseller 3] — Our team's personal pick\\n\\nPlus, new members enjoy free shipping on their first order. No code needed — it's automatically applied.\\n\\n[Shop Bestsellers]\\n\\nWe can't wait to hear what you think,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 5 days for first purchase conversion", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Subscriber made their first purchase?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "any_cart_activity", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send education email with brand story and product guide",
        1720,
        "{subscriber_email}",
        "Why 50,000+ customers trust us — and a little something for you",
        "Hi {first_name},\\n\\nWe started with a simple idea: make products that people genuinely love. Today, 50,000+ customers trust us, and here's why.\\n\\nOur story: [Brief brand story paragraph]\\n\\nWhat makes us different:\\n- [Differentiator 1] — Quality guarantee\\n- [Differentiator 2] — Sustainable materials\\n- [Differentiator 3] — 30-day free returns\\n\\nAs a thank you for being here, here's 15% off your first order with code WELCOME15.\\n\\n[Shop with 15% Off]\\n\\nWe hope to earn your trust,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Converts new Mailchimp subscribers into first-time buyers through a targeted nurture sequence. AI extracts subscriber profile and engagement signals, fetches engagement stats and activity from Mailchimp, sends a first-purchase nudge featuring bestsellers with social proof and free shipping, waits 5 days, and follows up with a brand story email plus a 15% discount code for subscribers who still haven't converted.",
  },

  // ─── 182. Subscription Renewal Reminder (Pattern F) ───────────────────

  "Subscription Renewal Reminder": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber's subscription renewal date is approaching",
        "webhook_app",
        {
          source: "mailchimp",
          event: "ecommerce.subscription_renewal_approaching",
          howItStarts:
            "Fires when a subscriber's recurring subscription is within 7 days of its renewal date, prompting a reminder email sequence to confirm renewal and update payment if needed.",
          setup:
            "Connect your Mailchimp account with your subscription/e-commerce platform. Configure a webhook that fires when a subscription renewal date is within 7 days.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract subscription plan, renewal date, pricing, and usage summary",
        280,
        "You are a subscription management analyst. Parse the renewal webhook and extract subscriber_email, subscriber_name, plan_name, plan_price, billing_cycle (monthly/quarterly/annual), renewal_date, days_until_renewal, subscription_tenure_months, auto_renew_status (on/off), and payment_method_last4.",
        "Analyze this subscription renewal event: Subscriber's subscription is approaching renewal. Extract plan details, pricing, tenure, and payment status.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "plan_price", as: "plan_price", type: "string" },
          {
            field: "days_until_renewal",
            as: "days_until_renewal",
            type: "number",
          },
          {
            field: "auto_renew_status",
            as: "auto_renew_status",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Check subscriber payment status and order history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}/orders",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "total_items",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "orders[0].financial_status",
                customName: "last_payment_status",
                type: "string",
              },
              {
                name: "orders[0].order_total",
                customName: "last_order_total",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Auto-renew is OFF or payment method may need updating?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "auto_renew_status", operator: "equals", value: "off" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send renewal reminder with plan summary and action required",
        1000,
        "{subscriber_email}",
        "Your {plan_name} subscription renews in {days_until_renewal} days",
        "Hi {subscriber_name},\\n\\nJust a heads up — your {plan_name} subscription (${plan_price}/period) is set to renew in {days_until_renewal} days.\\n\\nYour subscription summary:\\n- Plan: {plan_name}\\n- Price: ${plan_price}\\n- Renewal date: {renewal_date}\\n- Auto-renew: {auto_renew_status}\\n\\nPlease make sure your payment method is up to date to avoid any interruption in service.\\n\\n[Review My Subscription]\\n[Update Payment Method]\\n\\nThank you for being a subscriber,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 4 days for payment confirmation", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "Renewal confirmed or payment updated?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "last_payment_status", operator: "equals", value: "paid" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send value recap and renewal urgency for unresolved subscribers",
        1720,
        "{subscriber_email}",
        "Don't lose your {plan_name} benefits — renewal in {days_until_renewal} days",
        "Hi {subscriber_name},\\n\\nYour {plan_name} subscription is about to expire, and we don't want you to miss out on the benefits you've been enjoying.\\n\\nWhat you'll lose without renewal:\\n- [Key Benefit 1]\\n- [Key Benefit 2]\\n- [Key Benefit 3]\\n- Your saved preferences and history\\n\\nRenewing takes less than a minute. Just click below to confirm your subscription continues.\\n\\n[Renew My Subscription Now]\\n\\nNeed help? Reply to this email and we'll sort it out.\\n\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Ensures subscription renewals go through smoothly via a Mailchimp reminder sequence. AI extracts plan details, pricing, and auto-renew status, checks payment status and order history, sends a renewal reminder with plan summary and payment update CTA, waits 4 days, and follows up with a value recap and urgency email highlighting benefits at risk for subscribers who haven't confirmed their renewal.",
  },

  // ─── 183. Post-Purchase Education (Pattern E) ─────────────────────────

  "Post-Purchase Education": {
    nodes: [
      trigger(
        "Mailchimp webhook: Customer completes a purchase",
        "webhook_app",
        {
          source: "mailchimp",
          event: "ecommerce.order_placed",
          howItStarts:
            "Fires when a customer completes a purchase through a Mailchimp-connected e-commerce store, initiating a post-purchase education sequence to maximize product value and satisfaction.",
          setup:
            "Connect your Mailchimp account with your e-commerce store. The webhook fires automatically when an order is placed and payment is confirmed.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract order details, product types, and customer profile",
        280,
        "You are a post-purchase experience specialist. Parse the order data and extract customer_email, customer_name, order_id, order_total, products_purchased (array with name, category, and sku), is_first_purchase (boolean), product_complexity (simple/moderate/complex based on product type), and recommended_content_type (quick-start/tutorial/care-guide).",
        "Analyze this purchase for education content planning: Order placed by customer. Extract product details and determine appropriate education content type based on product complexity.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "products_purchased",
            as: "products_purchased",
            type: "object",
          },
          {
            field: "is_first_purchase",
            as: "is_first_purchase",
            type: "boolean",
          },
          {
            field: "product_complexity",
            as: "product_complexity",
            type: "string",
          },
          {
            field: "recommended_content_type",
            as: "recommended_content_type",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch customer tenure, tags, and VIP status",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "orders_count",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "total_spent",
                customName: "lifetime_value",
                type: "number",
              },
              { name: "first_name", customName: "first_name", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "First-time buyer (needs onboarding education)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "is_first_purchase", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Mailchimp: Send product getting-started guide with setup tips and care instructions",
        1000,
        "{customer_email}",
        "Your order is on its way! Here's how to get the most from it",
        "Hi {first_name},\\n\\nThank you for your purchase! While your order is being prepared, here's everything you need to know to get the most from your new {products_purchased}.\\n\\nGetting Started:\\n1. [Setup Step 1] — Unbox and inspect\\n2. [Setup Step 2] — Initial configuration\\n3. [Setup Step 3] — First use tips\\n\\nCare & Maintenance:\\n- [Care Tip 1]\\n- [Care Tip 2]\\n- [Storage recommendation]\\n\\nNeed help? Our support team is here for you.\\n\\n[View Getting Started Guide]\\n[Contact Support]\\n\\nEnjoy,\\nThe Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 10 days for product delivery and initial use",
        1240,
        {
          delayDetails: { duration: 10, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Product delivered and customer has had time to use it?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "total_orders", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Mailchimp: Send advanced tips, community resources, and cross-sell recommendations",
        1720,
        "{customer_email}",
        "Level up: Advanced tips for your {products_purchased}",
        "Hi {first_name},\\n\\nYou've had your {products_purchased} for a while now — here are some pro tips to take your experience to the next level.\\n\\nAdvanced Tips:\\n- [Pro Tip 1] — Unlock hidden feature\\n- [Pro Tip 2] — Maximize performance\\n- [Pro Tip 3] — Common mistakes to avoid\\n\\nJoin Our Community:\\nConnect with other customers sharing tips and inspiration in our community forum.\\n\\nYou Might Also Love:\\n- [Complementary Product 1] — pairs perfectly\\n- [Complementary Product 2] — customer favorite combo\\n\\n[Read Advanced Guide]\\n[Join Community]\\n\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Educates customers after purchase to maximize product value and satisfaction via Mailchimp. AI extracts product details and determines content complexity, fetches customer tenure and VIP status, sends a getting-started guide with setup tips and care instructions, waits 10 days for delivery and initial use, then sends advanced tips, community resources, and cross-sell recommendations to deepen engagement.",
  },

  // ─── 184. Event or Webinar Invite Journey (Pattern I) ─────────────────

  "Event or Webinar Invite Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber matches event target segment criteria",
        "webhook_app",
        {
          source: "mailchimp",
          event: "segment.event_target_match",
          howItStarts:
            "Fires when a Mailchimp subscriber is identified as a match for an upcoming event or webinar target audience based on their tags, engagement history, or segment membership.",
          setup:
            "Connect your Mailchimp account and create a segment for your event target audience. Configure the automation to trigger when subscribers match the segment criteria.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract event details, speaker info, subscriber relevance, and RSVP link",
        280,
        "You are an event marketing specialist. Parse the event targeting data and extract subscriber_email, subscriber_name, event_name, event_date, event_time, event_type (webinar/workshop/conference/meetup), speaker_names (array), event_description, subscriber_relevance_score, topic_match_reason, and rsvp_url.",
        "Analyze this event invitation targeting: Subscriber matched event audience criteria. Extract event details, speaker information, and reasons why this subscriber is a good fit.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          {
            field: "topic_match_reason",
            as: "topic_match_reason",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Fetch subscriber engagement score and content preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "member_rating",
                customName: "engagement_score",
                type: "number",
              },
              {
                name: "stats.avg_open_rate",
                customName: "avg_open_rate",
                type: "number",
              },
              {
                name: "merge_fields.FNAME",
                customName: "first_name",
                type: "string",
              },
              {
                name: "tags_count",
                customName: "interest_tags",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "High-engagement subscriber (rating 4+)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "engagement_score", operator: "greater_than", value: "3" },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send personalized event invitation with topic relevance and speaker info",
        1000,
        "{subscriber_email}",
        "You're invited: {event_name} on {event_date}",
        "Hi {first_name},\\n\\nWe have an upcoming {event_type} we think you'll love, based on your interest in {topic_match_reason}.\\n\\n{event_name}\\nDate: {event_date}\\nFormat: Live {event_type}\\n\\nWhat you'll learn:\\n- [Key Takeaway 1]\\n- [Key Takeaway 2]\\n- [Key Takeaway 3]\\n\\nFeaturing expert speakers sharing real-world insights and actionable strategies.\\n\\nSpaces are limited — reserve yours today.\\n\\n[Register Now — It's Free]\\n\\nSee you there,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 3 days for registration response", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Subscriber registered for the event?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "interest_tags", operator: "greater_than", value: "2" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send event reminder with agenda highlights and bonus content preview",
        1720,
        "{subscriber_email}",
        "Last chance to join {event_name} — here's what you'll miss",
        "Hi {first_name},\\n\\nJust a quick reminder about {event_name} happening on {event_date}. We've finalized the agenda, and it's packed with value.\\n\\nHighlights you'll miss if you don't attend:\\n- [Session 1] — industry benchmark data\\n- [Session 2] — live Q&A with experts\\n- [Session 3] — downloadable playbook for attendees only\\n\\nPlus, all registrants get access to the recording and exclusive bonus materials.\\n\\n[Register Before It's Full]\\n\\nHope to see you there,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Drives event and webinar registrations through targeted Mailchimp invitations. AI extracts event details, speaker info, and subscriber relevance scoring, fetches engagement scores and content preferences, sends a personalized invitation highlighting topic match and key takeaways, waits 3 days, and follows up with an agenda preview and bonus content tease for subscribers who haven't registered yet.",
  },

  // ─── 185. Survey Follow-Up Journey (Pattern I) ────────────────────────

  "Survey Follow-Up Journey": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber completes a survey or feedback form",
        "webhook_app",
        {
          source: "mailchimp",
          event: "survey.completed",
          howItStarts:
            "Fires when a subscriber completes a survey or feedback form connected to a Mailchimp campaign, providing structured response data for follow-up personalization.",
          setup:
            "Connect your Mailchimp account and embed a survey (e.g., Outgrow Forms, SurveyMonkey) linked to your Mailchimp list. Configure the webhook to fire when survey responses are submitted.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract survey responses, sentiment, and key themes from feedback",
        280,
        "You are a customer feedback analyst. Parse the survey response data and extract subscriber_email, subscriber_name, survey_name, overall_satisfaction (1-10), sentiment (positive/neutral/negative), key_themes (array of topics mentioned), specific_feedback_text, improvement_suggestions (array), and willingness_to_recommend (1-10).",
        "Analyze this survey completion: Subscriber submitted feedback. Extract sentiment, key themes, satisfaction scores, and actionable insights from their responses.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "overall_satisfaction",
            as: "overall_satisfaction",
            type: "number",
          },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "key_themes", as: "key_themes", type: "object" },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "object",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Tag subscriber based on survey sentiment and update profile",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}/tags",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "tags",
                value:
                  '[{"name":"survey-completed","status":"active"},{"name":"sentiment-{sentiment}","status":"active"}]',
              },
            ],
            variables: [
              { name: "tags", customName: "applied_tags", type: "object" },
            ],
          },
        },
      ),
      n("4", "condition", "Positive sentiment (satisfaction score > 7)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "overall_satisfaction",
              operator: "greater_than",
              value: "7",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send thank-you with personalized content based on survey themes",
        1000,
        "{subscriber_email}",
        "Thank you for your feedback, {subscriber_name}!",
        "Hi {subscriber_name},\\n\\nThank you so much for taking the time to share your thoughts! Your feedback about {key_themes} is incredibly valuable to us.\\n\\nBased on what you shared, here are some resources we think you'll find helpful:\\n\\n- [Resource 1] — Directly related to {key_themes[0]}\\n- [Resource 2] — Tips and best practices\\n- [Resource 3] — Deep dive into your area of interest\\n\\nYour input helps us improve, and we're already working on the areas you mentioned.\\n\\n[Explore Resources]\\n\\nWith gratitude,\\nThe Team",
      ),
      n("6", "delayAction", "Wait 5 days for content engagement", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Subscriber engaged with follow-up content?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "sentiment", operator: "equals", value: "positive" },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send progressive content or improvement update based on feedback themes",
        1720,
        "{subscriber_email}",
        "We listened: Updates based on feedback like yours",
        "Hi {subscriber_name},\\n\\nRemember the feedback you shared with us? We wanted to follow up and let you know what we've been working on.\\n\\nBased on feedback from customers like you:\\n- [Improvement 1] — Now live\\n- [Improvement 2] — Coming next month\\n- [Improvement 3] — In our roadmap\\n\\nYour voice matters, and we're committed to making things better. Here's a deeper look at what's changing:\\n\\n[Read Our Latest Updates]\\n\\nThank you for helping us improve,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Follows up on survey completions with personalized content through Mailchimp. AI extracts satisfaction scores, sentiment, and key themes from responses, tags the subscriber in Mailchimp with sentiment data, sends a personalized thank-you email with resources matched to their feedback themes, waits 5 days, and sends a follow-up showing how their feedback is being addressed with improvement updates and deeper content.",
  },

  // ─── 186. Customer Feedback to Testimonial Ask (Pattern J) ────────────

  "Customer Feedback to Testimonial Ask": {
    nodes: [
      trigger(
        "Mailchimp webhook: Customer submits positive feedback or high rating",
        "webhook_app",
        {
          source: "mailchimp",
          event: "feedback.positive_received",
          howItStarts:
            "Fires when a customer submits positive feedback (rating 8+ out of 10) or a positive review through a Mailchimp-connected feedback form, identifying them as a testimonial candidate.",
          setup:
            "Connect your Mailchimp account and configure a webhook on your feedback or review collection form. The automation triggers when a response with a rating of 8 or higher is received.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch customer profile, purchase history, and advocacy status",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "email_address",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "first_name",
                customName: "customer_name",
                type: "string",
              },
              {
                name: "orders_count",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "total_spent",
                customName: "lifetime_value",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Repeat customer (2+ orders) and not recently asked for testimonial?",
        520,
        {
          conditionDetails: {
            conditions: [
              { field: "total_orders", operator: "greater_than", value: "1" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Mailchimp: Send testimonial request with specific ask and easy submission format",
        760,
        "{customer_email}",
        "We'd love to share your story, {customer_name}",
        "Hi {customer_name},\\n\\nWe were thrilled to see your amazing feedback! Your words mean the world to us, and we'd love to share your experience with others who might benefit.\\n\\nWould you be open to providing a short testimonial? Here's what we'd love to hear:\\n\\n1. What problem were you trying to solve?\\n2. How has our product helped?\\n3. What would you tell someone considering us?\\n\\nIt only takes 2 minutes, and you can reply directly to this email or use the form below.\\n\\n[Share My Story — 2 Min Form]\\n\\nAs a thank you, you'll receive a $20 store credit for your next purchase.\\n\\nGratefully,\\nThe Team",
      ),
      n("5", "delayAction", "Wait 5 days for testimonial submission", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Customer submitted a testimonial?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "lifetime_value", operator: "greater_than", value: "200" },
          ],
        },
      }),
      emailNode(
        "7",
        "Mailchimp: Send gentle reminder with alternative testimonial format options",
        1480,
        "{customer_email}",
        "Quick reminder: Your story could help someone like you",
        "Hi {customer_name},\\n\\nWe know life gets busy! Just a gentle nudge about the testimonial we mentioned. If writing isn't your thing, we have other easy options:\\n\\n- Reply with just 1-2 sentences — we'll format it\\n- Record a 30-second video on your phone\\n- Let us interview you for 5 minutes (we'll do all the work)\\n\\nYour $20 store credit is waiting for you as a thank you.\\n\\n[Choose My Preferred Format]\\n\\nNo pressure at all,\\nThe Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log testimonial request status and customer details",
        1720,
        "Testimonial_Tracking",
        '[["{customer_email}","{customer_name}","{total_orders}","{lifetime_value}","testimonial_requested","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Converts positive customer feedback into testimonials through a structured Mailchimp ask journey. Fetches customer profile and purchase history, checks eligibility (repeat buyer, not recently asked), sends a testimonial request with specific prompts and a $20 store credit incentive, waits 5 days, sends a reminder offering alternative formats (written, video, interview), and logs the request status and outcomes to Google Sheets.",
  },

  // ─── 187. Omnichannel Journey with Retargeting Ads (Pattern I) ────────

  "Omnichannel Journey with Retargeting Ads": {
    nodes: [
      trigger(
        "Mailchimp webhook: Subscriber shows high engagement signal across channels",
        "webhook_app",
        {
          source: "mailchimp",
          event: "engagement.multi_channel_signal",
          howItStarts:
            "Fires when a subscriber demonstrates high engagement (multiple email opens, website visits, or ad clicks) across Mailchimp-connected channels, indicating readiness for an omnichannel conversion push.",
          setup:
            "Connect your Mailchimp account with Facebook/Google ad integrations and website tracking. Configure the webhook to fire when multi-channel engagement thresholds are met.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract engagement channels, product interests, and conversion readiness",
        280,
        "You are an omnichannel marketing analyst. Parse the multi-channel engagement data and extract subscriber_email, subscriber_name, engaged_channels (array: email/website/social/ads), top_product_interests (array), engagement_intensity (low/medium/high), last_email_opened_date, website_pages_viewed (array), ad_clicks_count, and estimated_purchase_intent (1-10).",
        "Analyze this multi-channel engagement signal: Subscriber is active across email, website, and ads. Extract channel behavior, product interests, and conversion readiness indicators.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "engaged_channels", as: "engaged_channels", type: "object" },
          {
            field: "top_product_interests",
            as: "top_product_interests",
            type: "object",
          },
          {
            field: "engagement_intensity",
            as: "engagement_intensity",
            type: "string",
          },
          {
            field: "estimated_purchase_intent",
            as: "estimated_purchase_intent",
            type: "number",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Mailchimp: Sync subscriber to retargeting ad audience for Facebook/Google",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://usX.api.mailchimp.com/3.0/lists/{list_id}/members/{subscriber_hash}/tags",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "tags",
                value:
                  '[{"name":"retarget-active","status":"active"},{"name":"high-intent","status":"active"}]',
              },
            ],
            variables: [
              {
                name: "tags",
                customName: "retarget_tags_applied",
                type: "object",
              },
            ],
          },
        },
      ),
      n("4", "condition", "High purchase intent (score > 7)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "estimated_purchase_intent",
              operator: "greater_than",
              value: "7",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Mailchimp: Send personalized email with products seen across channels",
        1000,
        "{subscriber_email}",
        "We noticed you browsing, {subscriber_name} — here's a curated selection",
        "Hi {subscriber_name},\\n\\nYou've been exploring some great products across our site and emails, and we wanted to make it easy for you to pick up where you left off.\\n\\nYour top picks based on your browsing:\\n- [Product 1 from {top_product_interests}] — most viewed\\n- [Product 2] — similar to what you liked\\n- [Product 3] — trending in your category\\n\\nPlus, as a thank you for being an engaged member, enjoy free shipping on your next order.\\n\\n[Shop Your Curated Selection]\\n\\nHappy shopping,\\nThe Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 4 days while retargeting ads run in parallel",
        1240,
        {
          delayDetails: { duration: 4, unit: "days" },
        },
      ),
      n("7", "condition", "Subscriber converted (made a purchase)?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "engagement_intensity",
              operator: "equals",
              value: "high",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Mailchimp: Send conversion push with social proof and limited-time offer",
        1720,
        "{subscriber_email}",
        "Everyone's talking about {top_product_interests} — your exclusive offer inside",
        "Hi {subscriber_name},\\n\\nThe products you've been eyeing are getting a lot of love from other customers. Here's what people are saying:\\n\\n\"Best purchase I've made this year\" — verified buyer\\n\"Exceeded my expectations\" — 5-star review\\n\\nWe'd love for you to experience it too. Here's an exclusive 10% off just for you:\\n\\nUse code: OMNIVIP10 at checkout\\n\\nThis offer expires in 48 hours and can't be combined with other promotions.\\n\\n[Claim My 10% Off]\\n\\nDon't miss out,\\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Orchestrates an omnichannel conversion journey combining Mailchimp email with retargeting ads. AI extracts multi-channel engagement signals and purchase intent, syncs the subscriber to retargeting ad audiences via Mailchimp tags, sends a personalized email featuring products browsed across channels, runs retargeting ads in parallel for 4 days, and follows up with a social proof-driven conversion push including an exclusive discount code for unconverted subscribers.",
  },

  // ─── 188. Loyalty / Repeat Buyer Nurture (Pattern H) ──────────────────

  "Loyalty / Repeat Buyer Nurture": {
    nodes: [
      trigger(
        "Mailchimp webhook: Customer completes their 3rd or more purchase",
        "webhook_app",
        {
          source: "mailchimp",
          event: "ecommerce.repeat_purchase_milestone",
          howItStarts:
            "Fires when a customer completes their 3rd purchase (or any subsequent repeat purchase), qualifying them as a loyal repeat buyer and triggering a loyalty nurture sequence.",
          setup:
            "Connect your Mailchimp account with your e-commerce store. Configure the automation to trigger when a customer's order count reaches 3 or more.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Mailchimp: Fetch customer loyalty data, lifetime value, and purchase frequency",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://usX.api.mailchimp.com/3.0/ecommerce/stores/{store_id}/customers/{customer_id}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "email_address",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "first_name",
                customName: "customer_name",
                type: "string",
              },
              {
                name: "orders_count",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "total_spent",
                customName: "lifetime_value",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "VIP-tier customer (5+ orders or $500+ lifetime value)?",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                field: "lifetime_value",
                operator: "greater_than",
                value: "500",
              },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Mailchimp: Send loyalty reward email with exclusive perks and thank-you",
        760,
        "{customer_email}",
        "You're one of our most valued customers, {customer_name}!",
        "Hi {customer_name},\\n\\nWow — {total_orders} orders! You're officially one of our most loyal customers, and we couldn't be more grateful.\\n\\nAs a thank you, here are your exclusive loyalty perks:\\n\\n- 20% off your next order (code: LOYAL20)\\n- Free shipping for life on your account\\n- Early access to all new product launches\\n- Priority customer support\\n\\nYour loyalty summary:\\n- Total orders: {total_orders}\\n- Lifetime value: ${lifetime_value}\\n- Status: VIP Member\\n\\n[Shop with Your VIP Perks]\\n\\nThank you for being amazing,\\nThe Team",
      ),
      n("5", "delayAction", "Wait 7 days for reward redemption", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("6", "condition", "Customer redeemed loyalty reward?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "total_orders", operator: "greater_than", value: "5" },
          ],
        },
      }),
      emailNode(
        "7",
        "Mailchimp: Send VIP next-tier teaser or redemption reminder",
        1480,
        "{customer_email}",
        "Your VIP perks are waiting, {customer_name} — plus a sneak peek",
        "Hi {customer_name},\\n\\nJust a reminder that your exclusive 20% off loyalty reward (code: LOYAL20) is still waiting for you!\\n\\nPlus, here's a sneak peek at what's coming for our top-tier VIPs:\\n\\n- Exclusive VIP-only products\\n- Invite to our annual VIP appreciation event\\n- Personal shopping assistant\\n- Birthday surprise package\\n\\nYou're just {orders_to_next_tier} orders away from unlocking these elite perks.\\n\\n[Use My 20% Off Now]\\n[Learn About VIP Tiers]\\n\\nYou deserve the best,\\nThe Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log loyalty milestone, reward status, and VIP tier",
        1720,
        "Loyalty_Milestones",
        '[["{customer_email}","{customer_name}","{total_orders}","{lifetime_value}","repeat_buyer_nurture","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Nurtures repeat buyers into loyal VIP customers through a Mailchimp loyalty journey. Fetches customer loyalty data including lifetime value and purchase frequency, checks VIP tier eligibility, sends a loyalty reward email with exclusive perks (20% discount, free shipping, early access), waits 7 days, sends a VIP next-tier teaser with redemption reminder for those who haven't used their reward, and logs all loyalty milestones and reward statuses to Google Sheets.",
  },
};
